import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { createBackofficeTag, fetchBackofficeTags, updateBackofficeTag } from './api';
import { TagEditorModal, TagsPageActions, TagsTable, TagsTableHeader } from './components';
import { PROTECTED_TAG_IDS } from './constants';
import type { BackofficeTag } from './types';
import type { TagColor } from '@/components/shared/tag-colors';

export function BackofficeTagsPage() {
  useBackofficePageTitle('Tags');

  const [tags, setTags] = useState<BackofficeTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<BackofficeTag | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingTagId, setUpdatingTagId] = useState<string | null>(null);

  const loadTags = useCallback(async (signal?: AbortSignal) => {
    const nextTags = await fetchBackofficeTags(signal);
    setTags(nextTags);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    loadTags(controller.signal)
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Failed to load tags');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [loadTags]);

  function openCreateModal() {
    setModalError(null);
    setSelectedTag(null);
    setEditorModalOpen(true);
  }

  function openEditModal(tag: BackofficeTag) {
    setModalError(null);
    setSelectedTag(tag);
    setEditorModalOpen(true);
  }

  async function handleSubmitTag(input: { name: string; description?: string; color: TagColor }) {
    if (submitting) return;

    setSubmitting(true);
    setModalError(null);
    try {
      if (selectedTag) {
        await updateBackofficeTag(selectedTag.id, {
          name: input.name,
          description: input.description ?? null,
          color: input.color,
        });
      } else {
        await createBackofficeTag(input);
      }
      setEditorModalOpen(false);
      setSelectedTag(null);
      await loadTags();
    } catch (caught) {
      setModalError(caught instanceof Error ? caught.message : 'Failed to save tag');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleStatus(tag: BackofficeTag) {
    setUpdatingTagId(tag.id);
    setModalError(null);
    try {
      const nextStatus = tag.status === 'active' ? 'archived' : 'active';
      await updateBackofficeTag(tag.id, { status: nextStatus });
      setSelectedTag({ ...tag, status: nextStatus });
      await loadTags();
    } catch (caught) {
      setModalError(caught instanceof Error ? caught.message : 'Failed to update tag');
    } finally {
      setUpdatingTagId(null);
    }
  }

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader actions={<TagsPageActions onCreateTag={openCreateModal} />}>Tags</PageHeader>

        <section className="rounded-xl border border-neutral-200 bg-white">
          <TagsTableHeader loading={loading} total={tags.length} />

          {loading ? (
            <div className="px-5 py-16 text-center text-body text-neutral-500">Loading tags</div>
          ) : error ? (
            <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
          ) : tags.length > 0 ? (
            <TagsTable tags={tags} onEditTag={openEditModal} />
          ) : (
            <div className="px-5 py-16 text-center text-body text-neutral-500">
              No tags created yet.
            </div>
          )}
        </section>

        {editorModalOpen ? (
          <TagEditorModal
            tag={selectedTag}
            submitting={submitting}
            statusUpdating={Boolean(selectedTag && updatingTagId === selectedTag.id)}
            protectedTag={Boolean(selectedTag && PROTECTED_TAG_IDS.has(selectedTag.id))}
            error={modalError}
            onClose={() => {
              if (!submitting && !updatingTagId) {
                setEditorModalOpen(false);
                setSelectedTag(null);
              }
            }}
            onSubmit={handleSubmitTag}
            onToggleStatus={() => {
              if (selectedTag) void handleToggleStatus(selectedTag);
            }}
          />
        ) : null}
      </div>
    </main>
  );
}
